/**
 * The agent's cursor over a browser tab, moved as Codex moves its own: short moves "scoot" in a
 * straight line, upright, with a sideways wobble and a squash along the move; long moves fly along
 * the best of a set of bezier paths, which arrive from the bottom right so that the arrow lands
 * tip first, turned along the path and stretched by its speed. On arrival (and when it first shows
 * up) the arrow wiggles as if thinking. Every channel is a spring, integrated at a fixed timestep.
 */
type BrowserAgentCursorMove = {
  sequence: number;
  x: number;
  y: number;
  /** `false` when the tab is not on screen: the cursor jumps there without moving. */
  animateMovement?: boolean;
};

export type BrowserAgentCursorController = {
  destroy(): void;
  move(next: BrowserAgentCursorMove): void;
  setActive(active: boolean): void;
};

type Point = { x: number; y: number };
type Size = { height: number; width: number };
type SpringOptions = { dampingFraction: number; response: number };
type Spring = SpringOptions & {
  force: number;
  scriptTime: number;
  simulationTime: number;
  target: number;
  value: number;
  velocity: number;
};
type Segment = { control1: Point; control2: Point; end: Point };
/** One or two cubic segments; two when the path bows out through an `arc` point. */
type MotionPath = { arc: Point | null; end: Point; segments: Segment[]; start: Point };
type PathMeasure = {
  angleChangeEnergy: number;
  length: number;
  maxAngleChange: number;
  staysInBounds: boolean;
  totalTurn: number;
};
type Motion =
  | { kind: 'path'; path: MotionPath; progress: Spring }
  | {
      axisRotation: number;
      end: Point;
      kind: 'scoot';
      progress: Spring;
      rotationTarget: number;
      start: Point;
    };
type CursorState = {
  motion: Motion | null;
  point: Point;
  positionX: Spring;
  positionY: Spring;
  rotation: number;
  rotationSpring: Spring;
  scootAxis: Spring;
  scootAxisRotation: number;
  scootRotation: Spring;
  scootStretch: Spring;
  stretch: Spring;
  thinkStartedAt: number | null;
  visibility: Spring;
};

const CURSOR_SIZE = 24;
const CURSOR_CENTER = CURSOR_SIZE / 2;
/** The agent's colour: the theme's blue, in a soft glow around the arrow. */
const CURSOR_GLOW_COLOR = 'var(--mode-blue, oklch(0.68 0.16 235))';
const CURSOR_GLOW_FILTER = [
  'drop-shadow(0 1px 1.5px rgb(0 0 0 / 0.35))',
  `drop-shadow(0 0 6px color-mix(in srgb, ${CURSOR_GLOW_COLOR} 90%, transparent))`,
  `drop-shadow(0 0 15px color-mix(in srgb, ${CURSOR_GLOW_COLOR} 48%, transparent))`,
].join(' ');
/**
 * A pointer arrow, tip at the top left: dark with a white outline, so that it reads on any page.
 * It is drawn turned by {@link ARTWORK_ROTATION}, which the cursor's resting rotation undoes.
 */
const CURSOR_ARTWORK = `<svg viewBox="0 0 23 24" width="23" height="24" fill="none" overflow="visible" xmlns="http://www.w3.org/2000/svg"><path d="M2.7 2.5 L17.84 8.4 Q19.16 8.92 17.84 9.54 L12.03 11.74 Q11.32 12.0 11.06 12.71 L8.86 18.52 Q8.33 19.84 7.89 18.43 Z" fill="#0B0B0D" stroke="#FFFFFF" stroke-width="2.6" stroke-linejoin="round" paint-order="stroke"/></svg>`;
const ARTWORK_ROTATION = 44;
/** The arrow's direction (its tip, up and to the left), in degrees clockwise from up. */
const CLICK_ANGLE = -44;
const REST_ROTATION = normalizeDegrees(CLICK_ANGLE);

const HIDDEN_BLUR = 5;
const HIDDEN_SCALE = 0.4;
const THINK_DURATION = 1.41;
const THINK_PERIOD = 0.66;
const THINK_AMPLITUDE = 12.5;
/** Where the cursor first shows up, as a fraction of the viewport. */
const DEFAULT_POSITION = { x: 0.58, y: 0.55 };
const FRAME = 1 / 60;
const ARRIVAL_DISTANCE = 0.85;
const ARRIVAL_SPEED = 12;
const SCOOT_DISTANCE = 196;
const SCOOT_ROTATION = 70;
const SCOOT_SQUASH = 0.15;
const TIMESTEP = 1 / 240;
const MAX_CATCH_UP = 1;
const REST_SPEED = 0.06;

const SPRINGS = {
  position: { dampingFraction: 0.9, response: 0.19 },
  rotation: { dampingFraction: 0.9, response: 0.12 },
  scootProgress: { dampingFraction: 0.94, response: 0.19 },
  scootRotation: { dampingFraction: 0.82, response: 0.055 },
  scootStretch: { dampingFraction: 0.86, response: 0.12 },
  stretch: { dampingFraction: 0.85, response: 0.2 },
  visibility: { dampingFraction: 0.86, response: 0.42 },
} satisfies Record<string, SpringOptions>;

/** How long paths are planned: handle lengths and arc sizes relative to the distance. */
const PATH = {
  arcFlow: 0.5783555327868779,
  arcSize: 0.2765523188064277,
  boundsMargin: 20,
  endHandle: 0.15,
  startHandle: 0.41960295031576633,
};
const PATH_DAMPING = 0.9;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function lerp(start: number, end: number, progress: number): number {
  return start + (end - start) * progress;
}

/** Degrees in [0, 360). */
function normalizeDegrees(value: number): number {
  const turned = value % 360;
  return turned < 0 ? turned + 360 : turned;
}

/** The shortest turn from one angle to another, in degrees. */
function angleDelta(from: number, to: number): number {
  let delta = to - from;
  while (delta > 180) delta -= 360;
  while (delta < -180) delta += 360;
  return delta;
}

function radiansDelta(from: number, to: number): number {
  let delta = to - from;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  return delta;
}

function distance(from: Point, to: Point): number {
  return Math.hypot(to.x - from.x, to.y - from.y);
}

function unit(vector: Point): Point {
  const length = Math.hypot(vector.x, vector.y);
  return length < 0.001 ? { x: 1, y: 0 } : { x: vector.x / length, y: vector.y / length };
}

function round(value: number): number {
  return Math.round(value * 1_000) / 1_000;
}

function now(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

// Springs

function spring(value: number, target: number, options: SpringOptions): Spring {
  return { ...options, force: 0, scriptTime: 0, simulationTime: 0, target, value, velocity: 0 };
}

/** Puts the spring at rest on `value`. */
function snap(state: Spring, value: number): void {
  Object.assign(state, {
    force: 0,
    scriptTime: 0,
    simulationTime: 0,
    target: value,
    value,
    velocity: 0,
  });
}

/** Aims an angle spring at `angle` the short way round. */
function aimAngle(state: Spring, angle: number): void {
  state.target = state.value + angleDelta(state.value, angle);
}

function settled(state: Spring): boolean {
  if (Math.max(state.velocity ** 2, state.force ** 2) > REST_SPEED ** 2) return false;
  const tolerance = state.target * 0.01;
  const offset = state.target - state.value;
  return tolerance === 0 || offset ** 2 <= tolerance ** 2;
}

function atRest(state: Spring): boolean {
  return state.value === state.target && settled(state);
}

/** Advances the spring by `elapsed` seconds, in fixed velocity-Verlet steps. */
function advance(state: Spring, elapsed: number): void {
  const response = Math.max(0.001, state.response);
  const stiffness = Math.min((Math.PI * 2) ** 2 / response ** 2, 1 / (2 * TIMESTEP ** 2));
  const damping = Math.sqrt(stiffness) * 2 * state.dampingFraction;
  state.scriptTime += Math.max(0, elapsed);
  if (state.scriptTime - state.simulationTime > MAX_CATCH_UP)
    state.simulationTime = state.scriptTime - FRAME;
  while (state.simulationTime < state.scriptTime) {
    const velocity = state.velocity + state.force * (TIMESTEP / 2);
    state.value += velocity * TIMESTEP;
    state.force = velocity * -damping + (state.target - state.value) * stiffness;
    state.velocity = velocity + state.force * (TIMESTEP / 2);
    state.simulationTime += TIMESTEP;
  }
  if (settled(state)) state.value = state.target;
}

// Paths

/** The unit vector of an angle in degrees clockwise from up. */
function directionOf(degrees: number): Point {
  const radians = (degrees * Math.PI) / 180;
  return { x: Math.sin(radians), y: -Math.cos(radians) };
}

/** `from` moved `reach` along `direction`, stopped at the viewport's edge. */
function ray(bounds: Size, from: Point, direction: Point, reach: number): Point {
  let length = reach;
  if (direction.x < 0) length = Math.min(length, from.x / -direction.x);
  if (direction.x > 0) length = Math.min(length, (bounds.width - from.x) / direction.x);
  if (direction.y < 0) length = Math.min(length, from.y / -direction.y);
  if (direction.y > 0) length = Math.min(length, (bounds.height - from.y) / direction.y);
  return {
    x: from.x + direction.x * Math.max(0, length),
    y: from.y + direction.y * Math.max(0, length),
  };
}

function cubicPoint(start: Point, segment: Segment, progress: number): Point {
  const remaining = 1 - progress;
  const a = remaining ** 3;
  const b = 3 * remaining ** 2 * progress;
  const c = 3 * remaining * progress ** 2;
  const d = progress ** 3;
  return {
    x: start.x * a + segment.control1.x * b + segment.control2.x * c + segment.end.x * d,
    y: start.y * a + segment.control1.y * b + segment.control2.y * c + segment.end.y * d,
  };
}

function cubicTangent(start: Point, segment: Segment, progress: number): Point {
  const remaining = 1 - progress;
  const { control1, control2, end } = segment;
  return {
    x:
      3 * remaining ** 2 * (control1.x - start.x) +
      6 * remaining * progress * (control2.x - control1.x) +
      3 * progress ** 2 * (end.x - control2.x),
    y:
      3 * remaining ** 2 * (control1.y - start.y) +
      6 * remaining * progress * (control2.y - control1.y) +
      3 * progress ** 2 * (end.y - control2.y),
  };
}

/** The point and direction at `progress` along the whole path, each segment an equal share. */
function pathAt(path: MotionPath, progress: number): { point: Point; tangent: Point } {
  const clamped = clamp(progress, 0, 1);
  const position = clamped === 1 ? path.segments.length - 1 : clamped * path.segments.length;
  const index = Math.floor(position);
  const segment = path.segments[index] as Segment;
  const start = index === 0 ? path.start : (path.segments[index - 1] as Segment).end;
  const local = clamped === 1 ? 1 : position - index;
  return { point: cubicPoint(start, segment, local), tangent: cubicTangent(start, segment, local) };
}

/** The cursor's rotation moving along `tangent`: its tip leads. */
function rotationAlong(tangent: Point): number {
  if (Math.hypot(tangent.x, tangent.y) < 0.001) return REST_ROTATION;
  const direction = unit(tangent);
  return normalizeDegrees((Math.atan2(direction.y, direction.x) * 180) / Math.PI + 90);
}

function insideBounds(point: Point, bounds: Size, margin: number): boolean {
  return (
    point.x >= margin &&
    point.x <= bounds.width - margin &&
    point.y >= margin &&
    point.y <= bounds.height - margin
  );
}

function measurePath(path: MotionPath, bounds?: Size): PathMeasure {
  let length = 0;
  let angleChangeEnergy = 0;
  let maxAngleChange = 0;
  let totalTurn = 0;
  let heading: number | null = null;
  let staysInBounds = bounds === undefined || insideBounds(path.start, bounds, PATH.boundsMargin);
  let segmentStart = path.start;
  let previous = path.start;
  for (const segment of path.segments) {
    for (let step = 1; step <= 24; step += 1) {
      const point = cubicPoint(segmentStart, segment, step / 24);
      length += distance(previous, point);
      if (bounds !== undefined) staysInBounds &&= insideBounds(point, bounds, PATH.boundsMargin);
      if (distance(previous, point) > 0.01) {
        const nextHeading = Math.atan2(point.y - previous.y, point.x - previous.x);
        if (heading !== null) {
          const turn = radiansDelta(heading, nextHeading);
          angleChangeEnergy += turn * turn;
          maxAngleChange = Math.max(maxAngleChange, Math.abs(turn));
          totalTurn += Math.abs(turn);
        }
        heading = nextHeading;
      }
      previous = point;
    }
    segmentStart = segment.end;
  }
  return { angleChangeEnergy, length, maxAngleChange, staysInBounds, totalTurn };
}

/** How much the move goes against the arrow's direction (down and to the right), 0 to 1. */
function backwardness(path: MotionPath): number {
  const click = directionOf(CLICK_ANGLE);
  const direction = unit({ x: path.end.x - path.start.x, y: path.end.y - path.start.y });
  return clamp((-(direction.x * click.x + direction.y * click.y) - 0.08) / 0.92, 0, 1);
}

function pathCost(path: MotionPath, measure: PathMeasure): number {
  const detour = Math.max(0, measure.length / Math.max(1, distance(path.start, path.end)) - 1);
  return (
    measure.length +
    detour * 320 +
    measure.angleChangeEnergy * 140 +
    measure.maxAngleChange * 180 +
    measure.totalTurn * 18 +
    backwardness(path) * 90 +
    (path.arc === null ? 0 : 45)
  );
}

/**
 * The path a long move takes: it leaves along the arrow's direction and arrives from the opposite
 * side, so the arrow lands tip first; a single curve, or one bowed through an arc point either
 * side of the straight line. The cheapest candidate that stays clear of the edges wins.
 */
function planPath(start: Point, end: Point, bounds: Size): MotionPath {
  const click = directionOf(CLICK_ANGLE);
  const into = { x: -click.x, y: -click.y };
  const span = distance(start, end);
  const direction = unit({ x: end.x - start.x, y: end.y - start.y });
  const startReach = Math.max(48, Math.min(640, span * PATH.startHandle, span * 0.9));
  const endReach = Math.max(48, Math.min(640, span * PATH.endHandle, span * 0.9));
  const startControl = ray(bounds, start, click, startReach);
  const endControl = ray(bounds, end, into, endReach);
  const perpendicular = { x: -direction.y, y: direction.x };
  const side = perpendicular.x * click.x + perpendicular.y * click.y >= 0 ? 1 : -1;
  const naturalNormal = { x: perpendicular.x * side, y: perpendicular.y * side };
  const midpoint = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  const arcDistance = Math.max(50, Math.min(520, span * PATH.arcSize));
  const arcHandle = Math.max(38, Math.min(440, span * PATH.arcFlow));
  const single = (control1: Point, control2: Point): MotionPath => ({
    arc: null,
    end,
    segments: [{ control1, control2, end }],
    start,
  });
  const candidates: MotionPath[] = [
    single(startControl, endControl),
    single(ray(bounds, start, click, startReach * 0.65), ray(bounds, end, into, endReach * 0.65)),
  ];
  for (const distanceScale of [0.55, 0.8, 1.05])
    for (const handleScale of [0.65, 1, 1.35])
      for (const normal of [naturalNormal, { x: -naturalNormal.x, y: -naturalNormal.y }]) {
        const offset = arcDistance * distanceScale;
        const handle = arcHandle * handleScale;
        const arc = {
          x: midpoint.x + normal.x * offset + click.x * startReach * 0.16,
          y: midpoint.y + normal.y * offset + click.y * startReach * 0.16,
        };
        candidates.push({
          arc,
          end,
          segments: [
            {
              control1: startControl,
              control2: { x: arc.x - direction.x * handle, y: arc.y - direction.y * handle },
              end: arc,
            },
            {
              control1: { x: arc.x + direction.x * handle, y: arc.y + direction.y * handle },
              control2: endControl,
              end,
            },
          ],
          start,
        });
      }
  let best = candidates[0] as MotionPath;
  let bestCost = Number.POSITIVE_INFINITY;
  let fallback = best;
  let fallbackCost = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const measure = measurePath(candidate, bounds);
    const cost = pathCost(candidate, measure);
    if (cost < fallbackCost) {
      fallback = candidate;
      fallbackCost = cost;
    }
    if (measure.staysInBounds && cost < bestCost) {
      best = candidate;
      bestCost = cost;
    }
  }
  return bestCost === Number.POSITIVE_INFINITY ? fallback : best;
}

/** How long the flight takes: longer for long, winding or backward paths. */
function pathResponse(path: MotionPath): number {
  const measure = measurePath(path);
  const detour = Math.max(0, measure.length / Math.max(1, distance(path.start, path.end)) - 1);
  const lengthFactor = clamp((measure.length - 180) / 760, 0, 1);
  const complexity = clamp(
    clamp(detour / 0.55, 0, 1) * 0.42 +
      clamp(measure.totalTurn / (Math.PI * 1.4), 0, 1) * 0.38 +
      clamp(measure.angleChangeEnergy / 1.25, 0, 1) * 0.2,
    0,
    1,
  );
  const arcBonus = path.arc === null ? 0 : 0.04;
  const arcScale = path.arc === null ? 1 : 0.9;
  return clamp(
    (0.42 + lengthFactor * 0.22 + complexity * 0.12 + backwardness(path) * 0.28 + arcBonus) *
      0.7 *
      arcScale,
    0.12,
    2.2,
  );
}

// Motion

function createState(point: Point, visible: boolean): CursorState {
  return {
    motion: null,
    point,
    positionX: spring(point.x, point.x, SPRINGS.position),
    positionY: spring(point.y, point.y, SPRINGS.position),
    rotation: REST_ROTATION,
    rotationSpring: spring(REST_ROTATION, REST_ROTATION, SPRINGS.rotation),
    scootAxis: spring(0, 0, SPRINGS.rotation),
    scootAxisRotation: 0,
    scootRotation: spring(0, 0, SPRINGS.scootRotation),
    scootStretch: spring(1, 1, SPRINGS.scootStretch),
    stretch: spring(1, 1, SPRINGS.stretch),
    thinkStartedAt: null,
    visibility: spring(visible ? 1 : 0, visible ? 1 : 0, SPRINGS.visibility),
  };
}

function setPositionSprings(state: CursorState, response: number, dampingFraction: number): void {
  for (const axis of [state.positionX, state.positionY])
    Object.assign(axis, { dampingFraction, response });
}

function placeAt(state: CursorState, point: Point): void {
  state.point = point;
  snap(state.positionX, point.x);
  snap(state.positionY, point.y);
}

function resetScoot(state: CursorState): void {
  snap(state.scootAxis, 0);
  snap(state.scootRotation, 0);
  snap(state.scootStretch, 1);
  state.scootAxisRotation = 0;
}

/** Jumps to `point`, upright and still. */
function teleport(state: CursorState, point: Point): void {
  state.motion = null;
  placeAt(state, point);
  snap(state.rotationSpring, REST_ROTATION);
  state.rotation = REST_ROTATION;
  resetScoot(state);
  snap(state.stretch, 1);
}

function startMotion(state: CursorState, end: Point, bounds: Size): void {
  state.thinkStartedAt = null;
  const start = { ...state.point };
  if (distance(start, end) <= SCOOT_DISTANCE) {
    const direction = unit({ x: end.x - start.x, y: end.y - start.y });
    const axisRotation =
      distance(start, end) < 0.001 ? 0 : (Math.atan2(direction.y, direction.x) * 180) / Math.PI;
    setPositionSprings(state, SPRINGS.position.response, SPRINGS.position.dampingFraction);
    state.positionX.target = end.x;
    state.positionY.target = end.y;
    aimAngle(state.rotationSpring, REST_ROTATION);
    aimAngle(state.scootAxis, axisRotation);
    state.motion = {
      axisRotation,
      end,
      kind: 'scoot',
      progress: spring(0, 1, SPRINGS.scootProgress),
      rotationTarget: clamp(direction.x * 0.75 - direction.y * 0.62, -1, 1) * SCOOT_ROTATION,
      start,
    };
    return;
  }
  const path = planPath(start, end, bounds);
  const response = pathResponse(path);
  setPositionSprings(state, clamp(response * 0.18, 0.035, 0.12), PATH_DAMPING);
  state.motion = {
    kind: 'path',
    path,
    progress: spring(0, 1, { dampingFraction: PATH_DAMPING, response }),
  };
}

/** Advances position, rotation and scoot axis; returns the new point and its speed. */
function followTargets(state: CursorState, elapsed: number): { point: Point; speed: number } {
  const previous = state.point;
  for (const channel of [state.positionX, state.positionY, state.rotationSpring, state.scootAxis])
    advance(channel, elapsed);
  const point = { x: state.positionX.value, y: state.positionY.value };
  state.point = point;
  state.rotation = state.rotationSpring.value;
  state.scootAxisRotation = state.scootAxis.value;
  return { point, speed: distance(previous, point) / Math.max(elapsed, TIMESTEP) };
}

function reached(state: CursorState, point: Point): boolean {
  return (
    distance(state.point, point) <= ARRIVAL_DISTANCE &&
    Math.abs(state.positionX.velocity) <= ARRIVAL_SPEED &&
    Math.abs(state.positionY.velocity) <= ARRIVAL_SPEED
  );
}

/** Where along the straight move `point` lies, 0 to 1. */
function projectedProgress(point: Point, start: Point, end: Point): number {
  const span = { x: end.x - start.x, y: end.y - start.y };
  const squared = span.x ** 2 + span.y ** 2;
  if (squared < 0.001) return 1;
  return clamp(((point.x - start.x) * span.x + (point.y - start.y) * span.y) / squared, 0, 1);
}

/** One frame of the current move; true when it just arrived. */
function advanceMotion(state: CursorState, elapsed: number, timestamp: number): boolean {
  const motion = state.motion;
  if (motion === null) {
    state.stretch.target = 1;
    state.scootStretch.target = 1;
    state.scootRotation.target = 0;
    return false;
  }
  state.thinkStartedAt = null;
  advance(motion.progress, elapsed);
  if (motion.kind === 'path') {
    state.scootStretch.target = 1;
    state.scootRotation.target = 0;
    const progress = clamp(motion.progress.value, 0, 1);
    const current = pathAt(motion.path, progress);
    state.positionX.target = current.point.x;
    state.positionY.target = current.point.y;
    aimAngle(state.rotationSpring, rotationAlong(current.tangent));
    aimAngle(state.scootAxis, 0);
    const { speed } = followTargets(state, elapsed);
    state.stretch.target = clamp(1 - speed / 5_500, 0.65, 1);
    if (
      progress < 0.999 ||
      Math.abs(motion.progress.velocity) >= 0.01 ||
      !reached(state, current.point)
    )
      return false;
    const last = pathAt(motion.path, 1);
    placeAt(state, last.point);
    state.rotation = rotationAlong(last.tangent);
    snap(state.rotationSpring, state.rotation);
    snap(state.scootAxis, 0);
    state.scootAxisRotation = 0;
  } else {
    state.positionX.target = motion.end.x;
    state.positionY.target = motion.end.y;
    aimAngle(state.scootAxis, motion.axisRotation);
    aimAngle(state.rotationSpring, REST_ROTATION);
    const progress = projectedProgress(
      followTargets(state, elapsed).point,
      motion.start,
      motion.end,
    );
    const wave = Math.sin(Math.min(1, progress) * Math.PI);
    state.stretch.target = 1;
    state.scootStretch.target = 1 - SCOOT_SQUASH * wave;
    state.scootRotation.target = motion.rotationTarget * wave;
    if (
      progress < 0.999 ||
      Math.abs(motion.progress.velocity) >= 0.01 ||
      !reached(state, motion.end)
    )
      return false;
    placeAt(state, motion.end);
    snap(state.rotationSpring, REST_ROTATION);
    state.rotation = REST_ROTATION;
    resetScoot(state);
  }
  snap(state.stretch, 1);
  state.motion = null;
  state.thinkStartedAt = timestamp;
  return true;
}

function animating(state: CursorState): boolean {
  return (
    state.motion !== null ||
    state.thinkStartedAt !== null ||
    [
      state.positionX,
      state.positionY,
      state.rotationSpring,
      state.scootAxis,
      state.scootRotation,
      state.scootStretch,
      state.stretch,
      state.visibility,
    ].some((channel) => !atRest(channel))
  );
}

/** The rotation with the thinking wiggle: a fading sine over {@link THINK_DURATION}. */
function thinkingRotation(state: CursorState, timestamp: number): number {
  if (state.thinkStartedAt === null) return state.rotation;
  const elapsed = (timestamp - state.thinkStartedAt) / 1_000;
  if (elapsed < 0) return state.rotation;
  const progress = Math.min(1, elapsed / THINK_DURATION);
  if (progress >= 1) {
    state.thinkStartedAt = null;
    return state.rotation;
  }
  const wiggle = Math.sin((elapsed / THINK_PERIOD) * Math.PI * 2) * Math.sin(progress * Math.PI);
  return state.rotation + wiggle * THINK_AMPLITUDE;
}

function render(cursor: HTMLElement, state: CursorState): void {
  const shown = clamp(state.visibility.value, 0, 1);
  const scale = lerp(HIDDEN_SCALE, 1, shown);
  const squash = clamp(state.scootStretch.value, 0, 1);
  const axis = state.scootAxisRotation;
  const rotation = normalizeDegrees(thinkingRotation(state, now()) + state.scootRotation.value);
  const transform = [
    `translate3d(${round(state.point.x - CURSOR_CENTER)}px, ${round(state.point.y - CURSOR_CENTER)}px, 0)`,
  ];
  if (Math.abs(angleDelta(0, axis)) > 0.001 || Math.abs(squash - 1) > 0.001)
    transform.push(
      `rotate(${round(axis)}deg)`,
      `scale(1, ${round(squash)})`,
      `rotate(${round(-axis)}deg)`,
    );
  transform.push(
    `rotate(${round(rotation)}deg)`,
    `scale(${round(state.stretch.value * scale)}, ${round(scale)})`,
  );
  cursor.style.transform = transform.join(' ');
  cursor.style.opacity = `${round(shown)}`;
  cursor.style.filter = `blur(${round(lerp(HIDDEN_BLUR, 0, shown))}px)`;
}

/**
 * The agent's cursor over a browser tab. It shows while the agent drives the tab (`setActive`):
 * at once, near the middle, wiggling, the first time; fading in and out afterwards. A `move` flies
 * there (or jumps, when `animateMovement` is false or the cursor has never been seen) and reports
 * its arrival once through `onArrived`, which the host waits on before acting.
 */
export function createBrowserAgentCursor(
  host: HTMLElement,
  onArrived: (sequence: number) => void,
): BrowserAgentCursorController {
  const layer = document.createElement('div');
  layer.dataset['testid'] = 'browser-agent-cursor-overlay';
  layer.setAttribute('aria-hidden', 'true');
  Object.assign(layer.style, {
    inset: '0',
    overflow: 'hidden',
    pointerEvents: 'none',
    position: 'absolute',
    zIndex: '20',
  });
  const cursor = document.createElement('div');
  cursor.dataset['testid'] = 'browser-agent-cursor';
  Object.assign(cursor.style, {
    height: `${CURSOR_SIZE}px`,
    left: '0',
    opacity: '0',
    position: 'absolute',
    top: '0',
    transformOrigin: `${CURSOR_CENTER}px ${CURSOR_CENTER}px`,
    willChange: 'transform',
    width: `${CURSOR_SIZE}px`,
  });
  const offset = document.createElement('div');
  offset.style.transform = 'translate3d(12px, -2.5px, 0)';
  const artwork = document.createElement('div');
  artwork.dataset['testid'] = 'browser-agent-cursor-artwork';
  Object.assign(artwork.style, {
    filter: CURSOR_GLOW_FILTER,
    height: '24px',
    transform: `rotate(${ARTWORK_ROTATION}deg)`,
    transformOrigin: '0 0',
    width: '23px',
  });
  artwork.innerHTML = CURSOR_ARTWORK;
  offset.appendChild(artwork);
  cursor.appendChild(offset);
  layer.appendChild(cursor);
  host.appendChild(layer);

  let state: CursorState | null = null;
  let active = false;
  let target: BrowserAgentCursorMove | null = null;
  let animationFrame: number | null = null;
  let destroyed = false;
  let lastFrameAt = now();
  /** The first frame of a flight advances one fixed frame, however long since the last. */
  let fixedFrame = false;
  let reportedSequence: number | null = null;
  /** Shown at once the first time the agent drives the tab, and on its first move if unseen. */
  let appeared = false;
  let jumpedIn = false;

  const reportArrival = () => {
    if (target === null || reportedSequence === target.sequence) return;
    reportedSequence = target.sequence;
    onArrived(target.sequence);
  };

  const scheduleFrame = () => {
    if (animationFrame !== null || state === null || destroyed) return;
    animationFrame = requestAnimationFrame((timestamp) => {
      animationFrame = null;
      if (state === null || destroyed) return;
      const elapsed = fixedFrame ? FRAME : Math.max(FRAME, (timestamp - lastFrameAt) / 1_000);
      fixedFrame = false;
      lastFrameAt = timestamp;
      const arrived = advanceMotion(state, elapsed, timestamp);
      for (const channel of [
        state.visibility,
        state.stretch,
        state.scootStretch,
        state.scootRotation,
      ])
        advance(channel, elapsed);
      render(cursor, state);
      if (arrived) reportArrival();
      if (animating(state)) scheduleFrame();
    });
  };

  const update = () => {
    if (destroyed) return;
    const bounds = { height: host.clientHeight, width: host.clientWidth };
    const point = {
      x: clamp(target?.x ?? Math.round(bounds.width * DEFAULT_POSITION.x), 0, bounds.width),
      y: clamp(target?.y ?? Math.round(bounds.height * DEFAULT_POSITION.y), 0, bounds.height),
    };
    state ??= createState(point, active);
    state.visibility.target = active ? 1 : 0;
    if (active && target === null && !appeared) {
      appeared = true;
      snap(state.visibility, 1);
      state.thinkStartedAt = now();
    }
    if (target === null) {
      teleport(state, point);
      render(cursor, state);
      scheduleFrame();
      return;
    }
    const firstSight = active && state.visibility.value <= 0.001 && !jumpedIn;
    state.thinkStartedAt = null;
    if (target.animateMovement === false || firstSight || distance(state.point, point) < 0.5) {
      if (firstSight) {
        jumpedIn = true;
        snap(state.visibility, 1);
      }
      teleport(state, point);
      render(cursor, state);
      reportArrival();
      scheduleFrame();
      return;
    }
    startMotion(state, point, bounds);
    fixedFrame = true;
    render(cursor, state);
    scheduleFrame();
  };

  return {
    destroy() {
      destroyed = true;
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);
      animationFrame = null;
      layer.remove();
    },
    move(next) {
      target = {
        sequence: next.sequence,
        x: next.x,
        y: next.y,
        ...(next.animateMovement === false ? { animateMovement: false } : {}),
      };
      update();
    },
    setActive(next) {
      if (next === active) return;
      active = next;
      update();
    },
  };
}
